import { isDev } from './index';

let sentryPromise: Promise<typeof import('@sentry/nextjs')> | null = null;

const isSentryLoggingEnabledInDev =
  typeof process !== 'undefined' ? process.env.NEXT_PUBLIC_SENTRY_LOGGING_DEV === 'true' : false;

const isDevLoggingEnabled =
  typeof process !== 'undefined' && process.env.ENABLE_DEV_LOGS === 'true' && process.env.VERCEL_ENV !== 'production';

/** Browser console output — local development only. */
export const isClientConsoleLoggingEnabled = () => isDev();

async function loadSentry() {
  if (!sentryPromise) {
    sentryPromise = import('@sentry/nextjs');
  }
  return sentryPromise;
}

export async function logError(
  error: Error | string | undefined,
  fingerprint: string | null = null,
  tags?: Record<string, string>
) {
  if (!error) return;
  // biome-ignore lint/suspicious/noConsole: intentional dev logging
  if (isDevLoggingEnabled) console.error(error);
  if (!isDev() || isSentryLoggingEnabledInDev) {
    const Sentry = await loadSentry();
    Sentry.withScope((scope) => {
      if (fingerprint) {
        scope.setFingerprint(['custom-fingerprint', fingerprint]);
      }

      if (tags) {
        for (const [key, value] of Object.entries(tags)) {
          scope.setTag(key, value);
        }
      }

      Sentry.captureException(error);
    });
  }
}

export async function logMessage(...messages: unknown[]) {
  if (typeof process !== 'undefined' && process.env.NO_LOG === 'true') {
    return;
  }

  if (isSentryLoggingEnabledInDev || !isDev()) {
    const Sentry = await loadSentry();
    Sentry.captureMessage(messages.join(' '));
  }
}

/**
 * Does a console.warn only in local development.
 */
export function safeWarn(...messages: unknown[]) {
  // biome-ignore lint/suspicious/noConsole: intended functionality - to warn in dev only
  if (isClientConsoleLoggingEnabled()) console.warn(...messages);
}
